import { useEffect } from 'react';
import { useRef } from 'react';
import {
  Chat,
  SideBar,
  MessageMenu,
  ToastPortal,
  TTSPlayer,
  usePlatformStore,
  useChatListStore,
  registerSettingsStores,
} from '@edgerunner/shared';
import { useUserSettingsStore } from './stores/useUserSettingsStore';
import { useSearchSync } from './hooks/useSearchSync';
import { useAuthGate } from './hooks/useAuthGate';
import { AuthScreen } from './components/AuthScreen';
import { FileBrowser } from './components';
import { webExportService } from './adapters/webExportService';
import { ProfileMenu } from './components/ProfileMenu';
import { ProductFeedbackLauncher } from './components/ProductFeedbackLauncher';

import faviconUrl from '@edgerunner/shared/assets/icons/logo.ico';

// Force the browser to use the imported icon
const link: HTMLLinkElement = document.querySelector("link[rel~='icon']") || document.createElement('link');
link.rel = 'icon';
link.href = faviconUrl;
document.getElementsByTagName('head')[0].appendChild(link);

// Register platform-specific settings stores (called once at module load)
registerSettingsStores(useUserSettingsStore);

// Wrapper component — only rendered after auth passes in web mode
function AppContent() {
  const chatInputRef = useRef(null);

  // Initialize and sync search index
  useSearchSync();

  // Start data loading now that we're past auth
  useEffect(() => {
    void useChatListStore.getState().startInitialConnection();
  }, []);

  return (
    <div className="w-full h-screen bg-neutral-700 flex flex-row overflow-hidden">
      <SideBar fileBrowser={FileBrowser} exportService={webExportService} profileMenu={ProfileMenu} />
      <Chat chatInputRef={chatInputRef} />
      <MessageMenu chatInputRef={chatInputRef} />
      <ToastPortal />
      <ProductFeedbackLauncher />
    </div>
  );
}

function App() {
  const authStatus = useAuthGate();

  // Initialize platform and start backend readiness check on mount.
  // waitForBackend resolves deployment mode once the backend responds,
  // which unblocks the auth gate. Data loads are triggered on readiness.
  useEffect(() => {
    usePlatformStore.getState().initializePlatform();
    usePlatformStore.getState().waitForBackend();
  }, []);

  if (authStatus === 'loading') {
    return (
      <div className="fixed top-0 left-0 w-full h-full bg-neutral-600 flex items-center justify-center z-[10000]">
        <div className="w-10 h-10 border-4 border-neutral-400/30 border-t-red-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (authStatus === 'unauthenticated') {
    return <AuthScreen />;
  }

  return (
    <>
      <AppContent />
      <TTSPlayer />
    </>
  );
}

export default App;
